/**
 * Generic API Client for City Landing Pages
 *
 * This service handles all API communication for interest form submissions,
 * champion applications, and other city-specific endpoints.
 *
 * Usage:
 *   const client = new CityApiClient(cityConfig.api);
 *   await client.submitInterestForm(formData);
 */

export interface ApiConfig {
  baseUrl: string;
  routePrefix: string;
  healthEndpoint: string;
  interestEndpoint: string;
  championEndpoint: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export class CityApiClient {
  private config: ApiConfig;

  constructor(config: ApiConfig) {
    this.config = config;
  }

  /**
   * Health Check
   * Tests if the API is responding
   */
  async healthCheck(): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}${this.config.healthEndpoint}`);
      const data = await response.json();
      return {
        success: response.ok,
        message: data.message || 'API is healthy',
        data,
      };
    } catch (error) {
      return {
        success: false,
        message: 'API health check failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Submit Interest Form
   * Submits user interest data to city-specific endpoint
   */
  async submitInterestForm(formData: any): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}${this.config.interestEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      return {
        success: response.ok,
        message: data.message || 'Form submitted successfully',
        data,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Form submission failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Submit Champion Application
   * Submits champion application to city-specific endpoint
   */
  async submitChampionApplication(applicationData: any): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}${this.config.championEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      const data = await response.json();

      return {
        success: response.ok,
        message: data.message || 'Application submitted successfully',
        data,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Application submission failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

/**
 * Create API Client from City Config
 * Helper function to instantiate client with city config
 */
export function createApiClient(apiConfig: ApiConfig): CityApiClient {
  return new CityApiClient(apiConfig);
}
